// src/pages/ProfilePage.js

import React, { useState, useEffect, useCallback, useRef } from "react";
import Layout from "../components/Layout";
import PageContainer from "../components/common/PageContainer";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import useApi from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";

// --- Data ---
const countryCodes = [
  { name: "USA", code: "US", dial_code: "+1" },
  { name: "UK", code: "GB", dial_code: "+44" },
  { name: "India", code: "IN", dial_code: "+91" },
  // Add more countries as needed
];

// --- Helper Icons ---
const VerifiedIcon = () => (
  <svg
    className="h-4 w-4 mr-1.5"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);
const UnverifiedIcon = () => (
  <svg
    className="h-4 w-4 mr-1.5"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
      clipRule="evenodd"
    />
  </svg>
);
// --- End Helper Icons ---

const ProfilePage = () => {
  const { user: authUser, updateUserProfile, loadingAuth } = useAuth();
  const api = useApi();
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [phone, setPhone] = useState("");
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = useCallback(async () => {
    if (!authUser) {
      setError("User not authenticated.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { user: fetchedProfile } = await api.get(`profile`);
      setProfile(fetchedProfile);
      setName(fetchedProfile.name || "");
      setUsername(fetchedProfile.username || "");
      setPreviewImage(fetchedProfile.profilePic || null);

      // Split phone number into country code and local number
      const fullPhone = fetchedProfile.phone || "";
      const matchedCode = countryCodes.find((c) =>
        fullPhone.startsWith(c.dial_code)
      );
      if (matchedCode) {
        setCountryCode(matchedCode.dial_code);
        setPhone(fullPhone.substring(matchedCode.dial_code.length));
      } else {
        setPhone(fullPhone);
      }
    } catch (err) {
      setError(err.message || "Failed to load profile data.");
    } finally {
      setLoading(false);
    }
  }, [authUser, api]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    let base64Image = null;

    // Convert the image file to Base64 if a file is provided
    if (profileImageFile) {
      base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result); // Keep the full Base64 string, including the prefix
        reader.onerror = reject;
        reader.readAsDataURL(profileImageFile);
      });
    }

    try {
      // Prepare the payload as a valid JSON object
      const payload = {
        name,
        username,
        phone: `${countryCode}${phone}`, // Combine country code and phone
        profileImage: base64Image || null, // Include Base64-encoded image or null
      };

      // Call the `updateUserProfile` function from AuthContext
      const updatedUser = await updateUserProfile(payload);

      // Update the previewImage with the new profile picture URL
      if (updatedUser.profileImageUrl) {
        setPreviewImage(updatedUser.profileImageUrl);
      }

      setMessage("Profile updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to update profile.");
    }
  };

  const handleResendVerificationEmail = async () => {
    try {
      setError('');
      setMessage('');
      await api.post('auth/resend-verification', { email: profile.email });
      setMessage('Verification email sent successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to send verification email.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageContainer title="Account Settings">
          <LoadingSpinner />
        </PageContainer>
      </Layout>
    );
  }

  if (error && !profile) {
    return (
      <Layout>
        <PageContainer title="Error">
          <ErrorMessage message={error} />
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageContainer
        title="Account Settings"
        subtitle="Manage your personal information and preferences."
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-center">
                <img
                  src={
                    previewImage ||
                    `https://ui-avatars.com/api/?name=${
                      profile.name || "User"
                    }&background=e5e7eb&color=4b5563&size=128`
                  }
                  alt="Profile"
                  className="h-32 w-32 rounded-full object-cover mx-auto mb-4"
                />
                <h2 className="text-xl font-bold text-gray-900">
                  {profile.name}
                </h2>
                <p className="text-sm text-gray-500">@{profile.username}</p>
                <p className="mt-1 text-sm text-gray-500">{profile.email}</p>
                <div className="mt-2">
                  {profile.isEmailVerified ? (
                    <span className="inline-flex items-center bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                      <VerifiedIcon /> Email Verified
                    </span>
                  ) : (
                    <div>
                      <span className="inline-flex items-center bg-yellow-100 text-yellow-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                        <UnverifiedIcon /> Not Verified
                      </span>
                      <button
                        onClick={handleResendVerificationEmail}
                        className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Resend Verification Email
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <hr className="my-6 border-gray-200" />
              <div>
                <h3 className="text-md font-semibold text-gray-700">
                  Organization
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {profile.organizationName || "N/A"}
                </p>
                <h3 className="mt-4 text-md font-semibold text-gray-700">
                  Role
                </h3>
                <p className="mt-1 text-sm text-gray-600 capitalize">
                  {profile.role || "User"}
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Edit Form */}
          <div className="lg:col-span-2">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-lg shadow-md"
            >
              <div className="p-6 space-y-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Edit Information
                </h3>
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Username
                  </label>
                  <input
                    type="text"
                    name="username"
                    id="username"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-100 text-gray-500 sm:text-sm"
                    value={profile.email || ""}
                    disabled
                    readOnly
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Phone Number
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <select
                      id="country-code"
                      name="country-code"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="block w-28 border border-r-0 border-gray-300 bg-gray-50 rounded-l-md py-2 px-3 text-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      {countryCodes.map((c) => (
                        <option key={c.code} value={c.dial_code}>
                          {c.code} {c.dial_code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      className="flex-1 min-w-0 block w-full border border-gray-300 rounded-none rounded-r-md py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="555-123-4567"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Profile Photo
                  </label>
                  <div className="mt-1 flex items-center space-x-4">
                    <img
                      src={
                        previewImage ||
                        `https://ui-avatars.com/api/?name=${
                          name || "User"
                        }&background=e5e7eb&color=4b5563&size=64`
                      }
                      alt="Avatar preview"
                      className="h-16 w-16 rounded-full object-cover"
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      className="hidden"
                      accept="image/png, image/jpeg, image/jpg"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Change Photo
                    </button>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 flex justify-end items-center space-x-4 rounded-b-lg">
                {message && <p className="text-sm text-green-600">{message}</p>}
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button
                  type="submit"
                  disabled={loadingAuth}
                  className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400"
                >
                  {loadingAuth ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </PageContainer>
    </Layout>
  );
};

export default ProfilePage;
